import React, { useEffect, useState } from "react";
import FormTemplate from "../../../forms/FormTemplate";
import {
  Box,
  Button,
  Stack,
  Typography,
  Select,
  MenuItem,
  lighten,
  Grid,
} from "@mui/material";
import "../../../styles/SMSSideMenu.css";
import useAutomations from "../../../hooks/automationhooks/useAutomations";
import WhatsappTemplateSelection from "./WhatsappTemplateSelection";
import SelectArrow from "../../../styles/icons/SelectArrow";
import ROUTES from "../../../routerfolder/Routes";
import { useParams } from "react-router-dom";

export default function WhatsappSideMenu({
  onClose,
  onSubmit,
  selectedNode,
  preSelectedTemplate = null,
  isDrawer = false,
}) {
  //FOR AUTH CHECKS LATER
  const isAuthorized = false;
  const {
    isLoading,
    whatsappTemplates,
    handleGetWhatsappTemplates,
    setLoading,
    handleGetSingleWhatsappTemplate,
  } = useAutomations();
  const { brandId } = useParams();
  const [selected, setSelected] = useState(preSelectedTemplate);
  const [templateSelectionOpen, setTemplateSelectionOpen] = useState(false);
  const [valid, setValid] = useState(false);

  // Track placeholder values
  const [placeholderValues, setPlaceholderValues] = useState({});

  // State to hold the correct template message
  const [messageText, setMessageText] = useState("");
  const [templateParams, setTemplateParams] = useState([]); // Store template parameters
  const [templateButtons, setTemplateButtons] = useState([]);

  // Fetch templates when component mounts
  useEffect(() => {
    // ask the cache first; only fetch if we haven't yet
    if (!whatsappTemplates?.data?.Data?.List?.length) {
      handleGetWhatsappTemplates(brandId);
    }
  }, [brandId]); // ← runs once per brand

  useEffect(() => {
    if (whatsappTemplates) {
      setLoading(false);
    }
  }, [whatsappTemplates]);

  // Match the correct template based on selectedNode's `template.value`
  useEffect(() => {
    if (
      whatsappTemplates?.data?.Data?.List &&
      selectedNode?.data?.template?.value
    ) {
      const matchedTemplate = whatsappTemplates.data.Data.List.find(
        (template) => template.TemplateId === selectedNode.data.template.value
      );

      if (matchedTemplate) {
        setMessageText(matchedTemplate.MessageText);
        setTemplateParams(matchedTemplate.TemplateParameters || []);

        // Initialize placeholders: prioritize saved values, else default to "null"
        let initialPlaceholders = {};
        matchedTemplate.TemplateParameters.forEach((param) => {
          initialPlaceholders[param] =
            selectedNode?.data?.template?.placeholderMappings?.[param] ||
            "null";
        });

        setPlaceholderValues(initialPlaceholders);

        // If there are no parameters, mark it as valid
        setValid(matchedTemplate.TemplateParameters.length === 0);
      } else {
        setMessageText("");
        setTemplateParams([]);
        setValid(false);
      }
    }
  }, [whatsappTemplates, selectedNode]);

  // Check validity (All placeholders must be filled)
  useEffect(() => {
    if (templateParams.length > 0) {
      const allFilled = templateParams.every(
        (param) => placeholderValues[param] !== "null"
      );
      setValid(allFilled);
    }
  }, [placeholderValues, templateParams]);

  // Handle updates to placeholder selections
  const handlePlaceholderChange = (placeholderKey, value) => {
    setPlaceholderValues((prev) => ({
      ...prev,
      [placeholderKey]: value,
    }));
  };

  // Function to render message text with dropdowns replacing placeholders
  const renderMessageWithSelect = (text) => {
    return text.split(/(\[#\d+#\])/g).map((part, i) => {
      if (/\[#\d+#\]/.test(part)) {
        const placeholderKey = part; // The actual placeholder key in the text
        return (
          <Select
            key={i}
            value={placeholderValues[placeholderKey] || "null"} // Correctly loads saved values
            onChange={(e) =>
              handlePlaceholderChange(placeholderKey, e.target.value)
            }
            size="small"
            sx={{
              margin: "0 5px",
              fontSize: "0.875rem",
              mt: 0.5,
              pr: 0.5,
              borderRadius: 4,
            }}
            IconComponent={SelectArrow}
          >
            <MenuItem value="null" disabled>
              Select option
            </MenuItem>
            <MenuItem value="FirstName">First Name</MenuItem>
            <MenuItem value="LastName">Last Name</MenuItem>
            <MenuItem value="FullName">Full Name</MenuItem>
            <MenuItem value="Id">Id</MenuItem>
            <MenuItem value="Phone">Phone</MenuItem>
          </Select>
        );
      } else {
        return <span key={i}>{part}</span>; // Render normal text
      }
    });
  };

  // Handle template selection
  // Handle template selection
  const handleSelected = async (key) => {
    try {
      const fullTemplate = await handleGetSingleWhatsappTemplate(
        key.TemplateId
      );
      console.log("Full template response:", fullTemplate);

      setSelected(fullTemplate);
      setTemplateParams(key.TemplateParameters || []);
      setMessageText(key.MessageText);

      // Safely access nested properties with fallbacks
      const templateComponents =
        fullTemplate?.AdditionalData?.TemplateComponents;
      const buttons =
        templateComponents &&
        Array.isArray(templateComponents) &&
        templateComponents.length > 0
          ? templateComponents[0]?.buttons || []
          : [];

      setTemplateButtons(buttons);
      console.log("Template buttons:", buttons);

      // Merge new template with existing saved mappings
      let initialPlaceholders = {};
      (key.TemplateParameters || []).forEach((param) => {
        initialPlaceholders[param] =
          selectedNode?.data?.template?.placeholderMappings?.[param] || "null";
      });

      setPlaceholderValues(initialPlaceholders);

      // If there are no variables, mark it as valid
      setValid((key.TemplateParameters || []).length === 0);
      setTemplateSelectionOpen(false);
    } catch (error) {
      console.error("Error loading WhatsApp template:", error);
      // Handle error gracefully - maybe show a toast notification
      // For now, just set empty values to prevent further errors
      setTemplateButtons([]);
      setTemplateSelectionOpen(false);
    }
  };

  useEffect(() => {
    // Creation flow: template picked before the side-menu opens
    if (preSelectedTemplate && !selectedNode) {
      handleSelected(preSelectedTemplate); // re-use the same logic
    }
  }, [preSelectedTemplate]);

  const handleOpenSelection = () => {
    setTemplateSelectionOpen(true);
  };

  const handleClose = () => {
    setTemplateSelectionOpen(false);
  };

  // Save placeholder mappings when submitting
  const handleSave = () => {
    if (selectedNode) {
      selectedNode.data.template.placeholderMappings = placeholderValues;
      onSubmit(selected);
    } else {
      const newData = {
        ...selected,
        placeholderMappings: placeholderValues,
      };
      console.log(placeholderValues);

      console.log(newData);

      onSubmit(newData);
    }
    onClose();
  };
  return (
    <>
      {/* Prevent loading state from blocking the UI */}
      {isLoading && <Typography sx={{ p: 2 }}>Loading Templates...</Typography>}

      {!isLoading && (
        <>
          <FormTemplate
            title={selectedNode ? "Edit Whatsapp" : "Create Whatsapp"}
            isValid={valid}
            onClose={onClose}
            handleSubmit={handleSave}
          >
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Stack
                direction={"row"}
                spacing={2}
                sx={{ flexShrink: 0, marginBottom: 2 }}
              >
                <Button
                  variant="contained"
                  color="companyBlue"
                  sx={{
                    borderRadius: "16px", // Change the border radius here
                    overflow: "visible", // Prevent the rounded corners from being clipped
                    textTransform: "none",
                    boxShadow: 0,
                    color: "white",
                    "&:hover": {
                      backgroundColor: (theme) =>
                        lighten(theme.palette.companyBlue.main, 0.1), // Lightens the color by 20%
                    },
                  }}
                  onClick={handleOpenSelection}
                >
                  <Typography>Select Template</Typography>
                </Button>
                {isAuthorized ?? (
                  <Button
                    variant="outlined"
                    sx={{
                      borderRadius: "16px", // Change the border radius here
                      overflow: "visible", // Prevent the rounded corners from being clipped
                      textTransform: "none",
                      color: "white",
                      boxShadow: 0,
                    }}
                    onClick={() =>
                      window.open(
                        "#" +
                          ROUTES.TEMPLATES.replace(
                            ":TemplateType",
                            "Whatsapp"
                          ).replace(":brandId", brandId),
                        "_blank",
                        "rel=noopener noreferrer"
                      )
                    }
                  >
                    <Typography>Create New Template</Typography>
                  </Button>
                )}
              </Stack>

              {/* Conditional Rendering for Message Text */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                }}
              >
                {!messageText && (
                  <Typography color="#EB4D4D">No Template Selected</Typography>
                )}
              </Box>
              {messageText && (
                <Box>
                  <Typography>Selected Template:</Typography>
                  <Box
                    sx={{
                      border: 1,
                      borderRadius: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: 2,
                      boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                      position: "relative",
                      transition: "box-shadow 0.3s ease-in-out",
                      "&:hover": {
                        boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.3)",
                      },
                    }}
                  >
                    {/* Render Message Text with Select Dropdowns */}
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "100%",
                        textAlign: "center",
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {renderMessageWithSelect(messageText)}
                    </Typography>
                  </Box>
                </Box>
              )}
              {templateButtons && (
                <>
                  {templateButtons.map((button) => {
                    return (
                      <Box>
                        <Typography>Button Text: {button.text}</Typography>
                      </Box>
                    );
                  })}
                </>
              )}
            </Box>
          </FormTemplate>
          <WhatsappTemplateSelection
            open={templateSelectionOpen}
            setSelected={handleSelected}
            setClosed={handleClose}
            templates={whatsappTemplates?.data?.Data?.List || []}
            refreshTemplates={handleGetWhatsappTemplates}
            isDrawer={isDrawer}
          />
        </>
      )}
    </>
  );
}
